import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { ArrowLeft, BookOpen, Users, Calendar, Mail, ShieldCheck, BarChart3, ChevronRight } from 'lucide-react';

const SECTIONS = [
  {
    icon: Calendar,
    title: 'Shifts & Operations Calendar',
    faqs: [
      ['How do I create a shift?', 'Admin Dashboard → Jobs/Shifts → New Job/Shift. Fill venue, date, role, rate, and talent count. Save.'],
      ['How do I assign talent?', 'Open a shift detail page → Staffing Management → Assign Primary/Backup/Extra. Use Invite Radius for batch invites.'],
      ['What do staffing statuses mean?', 'Open = no one booked. Partially filled = some seats booked. Filled = seats ≥ talent needed. Urgent = <48h away and not filled.'],
    ],
  },
  {
    icon: Users,
    title: 'Users & Impersonation',
    faqs: [
      ['Can I log in as a user?', 'Super admin only: Users → ⋯ → Login As User. A red banner confirms you are impersonating. Click Stop to exit.'],
      ['How do I approve a talent profile?', 'Users → open talent → Approve Profile. This also shortcuts IDV if unchecked.'],
    ],
  },
  {
    icon: ShieldCheck,
    title: 'ID Verification (IDV)',
    faqs: [
      ['Where do new IDVs appear?', 'Admin Dashboard → ID Verification. Pending Review is the default tab.'],
      ['What does reject do?', 'Sets status to Rejected and surfaces the reason to the talent on their profile. They can resubmit.'],
    ],
  },
  {
    icon: Mail,
    title: 'Broadcast & Templates',
    faqs: [
      ['How do I reach only talent within 50 miles?', 'Compose a broadcast → Filters → Radius 50mi. Preview recipients before Send.'],
      ['Can AI draft a broadcast?', 'Yes — Broadcast → Manage Templates → AI Draft. Pick audience + tone, describe purpose, edit, save.'],
      ['Why did a broadcast show "failed"?', 'Click the failed count badge in History to see per-recipient error messages (invalid email, Twilio bounce, etc).'],
    ],
  },
  {
    icon: BarChart3,
    title: 'Attendance & Compliance',
    faqs: [
      ['Where do I see clock-in selfies?', 'Admin Shift Detail → Attendance & Compliance section. Click a thumbnail for full view.'],
      ['How do I override a missed clock-out?', 'Same panel → Override Clock-Out. A reason is required and logged to the audit trail.'],
    ],
  },
];

const AdminHelp = () => {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-gray-50" data-testid="admin-help-page">
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
            <ArrowLeft className="w-4 h-4 mr-1" /> Back
          </Button>
          <h1 className="text-lg font-semibold flex items-center gap-2">
            <BookOpen className="w-5 h-5 text-cyan-600" />
            Docs & FAQs
          </h1>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 py-6 space-y-4">
        {SECTIONS.map((s) => {
          const Icon = s.icon;
          return (
            <Card key={s.title}>
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-base">
                  <Icon className="w-4 h-4 text-cyan-600" />
                  {s.title}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {s.faqs.map(([q, a], i) => (
                  <details key={i} className="group border-b border-gray-100 last:border-0 pb-2">
                    <summary className="cursor-pointer flex items-start gap-2 list-none text-sm font-medium text-gray-800">
                      <ChevronRight className="w-4 h-4 mt-0.5 text-gray-400 group-open:rotate-90 transition-transform" />
                      <span>{q}</span>
                    </summary>
                    <p className="ml-6 mt-1 text-sm text-gray-600">{a}</p>
                  </details>
                ))}
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
};

export default AdminHelp;
